import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { saveData } from 'services/api/saveData';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import TaskCheckmark from 'assets/TaskCheckmark.svg';
import '../../style/scss/check-button.scss';

const CheckButton: React.FC<{ 
    taskId: number;
    checked: boolean;
    onSuccess: () => void;
}> = ({ 
    taskId, checked, onSuccess 
}) => {
    const { t } = useTranslation();
    const { userHash } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const [localChecked, setLocalChecked] = useState<boolean | null>(null);

    // Determine the current checked status
    const isChecked = localChecked !== null ? localChecked : checked;

    // Handle the check click
    const handleCheckClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Set the local checked state, to directly show the check icon before refreshing the list
        setLocalChecked(!isChecked);

        // Convert the current date time into utc based on the timezone of the user
        const currentDateTime = new Date();
        const utcDateTime = convertLocalDateTimeToUTC(currentDateTime, userLocale, userTimezone);

        // Configure the submit data
        const submitData = {
            checked: !isChecked,
            checked_at: !isChecked ? utcDateTime : null,
            checked_by_user_hash: !isChecked ? userHash : null,
        }

        try {
            // Call the backend
            const response = await saveData({
                apiUrl: `patch_task/${taskId}`,
                method: 'patch',
                data: submitData,
            });
    
            // Handle successful response
            if (response?.status === 200) {

                // Determine the right alert message
                const alertMessage = !isChecked ? 'task.general.task_checked_alert' : 'task.general.task_unchecked_alert';

                // Show success alert
                setFloatingAlert({ type: 'success', message: alertMessage });

                // Wait 300ms before calling the onsuccess to refresh the list
                setTimeout(() => {
                    onSuccess?.();
                }, 300);
            } else {
                // Reset the local checked state
                setLocalChecked(null);
            }
        } catch (err) {
            console.error("Error on patching", err);
            setLocalChecked(null);
        }
    };
  
    return (
        <button className={`check-button ${isChecked ? 'checked' : ''} tooltip-icon`}
                onClick={handleCheckClick}>
            {isChecked ? (
                <img src={TaskCheckmark} alt="Checked" /> 
            ) : (
                null
            )}
            <span className="tooltip">
                {!isChecked
                    ? t('timeline.task.general.mark_done_label')
                    : t('timeline.task.general.mark_to_do_label')}
            </span>
        </button>
    );
};

export default CheckButton;